import React, { useRef, useState, useEffect, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';

import { MetricSession } from '../../models/CaseTypes';
import MeshViewer from '../CaseViewer/MeshViewer';
import { logTrace } from '../../utils/trackingUtils';

interface SpineViewerProps {
  id: string;
  metricSessions: MetricSession[];
  onSessionChange: (selectedSession: string) => void;
  session: MetricSession | null;
  signedUrl: string | null;
  selectedPlaneNames: string[];
  selectedVertebraeNames: string[];
}

const SpineViewer = (
  {
    id,
    metricSessions,
    onSessionChange,
    session,
    selectedPlaneNames,
    selectedVertebraeNames,
    signedUrl
  } : SpineViewerProps
): React.JSX.Element  => {

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);

  // Handle outside click to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
        setFocusedIndex(-1);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  // Focus first item when dropdown opens
  useEffect(() => {
    if (dropdownOpen && session) {
      const currentIndex = metricSessions.findIndex(s => s.sessionName === session.sessionName);
      setFocusedIndex(currentIndex >= 0 ? currentIndex : 0);
    }
  }, [dropdownOpen, session, metricSessions]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent): void => {
    switch (event.key) {
    case 'Enter':
    case ' ':
      event.preventDefault();
      if (!dropdownOpen) {
        setDropdownOpen(true);
      } else if (focusedIndex >= 0) {
        onSessionChange(metricSessions[focusedIndex].sessionName);
        setDropdownOpen(false);
        setFocusedIndex(-1);
        triggerRef.current?.focus();
      }
      break;
    case 'Escape':
      event.preventDefault();
      setDropdownOpen(false);
      setFocusedIndex(-1);
      triggerRef.current?.focus();
      break;
    case 'ArrowDown':
      event.preventDefault();
      if (!dropdownOpen) {
        setDropdownOpen(true);
      } else {
        setFocusedIndex(prev =>
          prev < metricSessions.length - 1 ? prev + 1 : prev
        );
      }
      break;
    case 'ArrowUp':
      event.preventDefault();
      if (dropdownOpen) {
        setFocusedIndex(prev => prev > 0 ? prev - 1 : prev);
      }
      break;
    case 'Home':
      event.preventDefault();
      if (dropdownOpen) {
        setFocusedIndex(0);
      }
      break;
    case 'End':
      event.preventDefault();
      if (dropdownOpen) {
        setFocusedIndex(metricSessions.length - 1);
      }
      break;
    case 'Tab':
      if (dropdownOpen) {
        setDropdownOpen(false);
        setFocusedIndex(-1);
      }
      break;
    }
  }, [dropdownOpen, focusedIndex, metricSessions, onSessionChange]);

  const handleOptionClick = (sessionName: string): void => {
    onSessionChange(sessionName);
    setDropdownOpen(false);
    setFocusedIndex(-1);
    triggerRef.current?.focus();
  };

  if (!session) {
    return (
      <div></div>
    );
  }

  const listboxId = `${id}-listbox`;

  return (
    <React.Fragment>
      <div ref={containerRef} className="relative h-11">
        <button
          ref={triggerRef}
          type="button"
          id={id}
          onClick={() => setDropdownOpen(!dropdownOpen)}
          onKeyDown={handleKeyDown}
          aria-haspopup="listbox"
          aria-expanded={dropdownOpen}
          aria-controls={listboxId}
          className="bg-white h-[44px] relative shrink-0 w-full cursor-pointer hover:bg-button-hover active:bg-button-active transition-colors text-left"
        >
          <div className="flex flex-row items-center overflow-clip size-full">
            <div className="box-border content-stretch flex gap-[10px] h-[44px] items-center px-[10px] py-0 relative w-full">
              <div className="basis-0 grow min-h-px min-w-px relative shrink-0">
                <div className="overflow-clip size-full">
                  <div className="box-border content-stretch flex items-start pb-[7px] pt-[5px] px-[2px] relative w-full">
                    <p className="font-['Open_Sans:Regular',sans-serif] font-normal leading-[normal] relative shrink-0 text-[#2a2a2a] text-[14px] text-nowrap whitespace-pre" style={{ fontVariationSettings: "'wdth' 100" }}>
                      { session.sessionDescription }
                    </p>
                  </div>
                </div>
              </div>
              <div className="box-border content-stretch flex gap-[4px] items-center justify-end pl-[2px] pr-0 py-[6px] relative shrink-0">
                <div className="relative shrink-0 size-[20px]">
                  <ChevronDown
                    className={`absolute inset-0 text-[#757575] transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
                    size={20}
                    aria-hidden="true"
                  />
                </div>
              </div>
            </div>
          </div>
        </button>
        <div className={`absolute ${dropdownOpen ? 'bg-[var(--primary)]' : 'bg-[#757575]'} bottom-0 h-px left-0 right-0`} />

        {/* Dropdown Menu */}
        {dropdownOpen && (
          <div
            ref={listboxRef}
            id={listboxId}
            role="listbox"
            aria-activedescendant={focusedIndex >= 0 ? `${id}-option-${focusedIndex}` : undefined}
            tabIndex={-1}
            onKeyDown={handleKeyDown}
            className="absolute top-[48px] left-0 right-0 z-50 box-border content-stretch flex flex-col items-start overflow-clip shadow-[0px_0px_20px_0px_rgba(0,0,0,0.2)]"
          >
            {metricSessions.map((selectOption, index) => (
              <div
                key={selectOption.sessionName}
                id={`${id}-option-${index}`}
                role="option"
                aria-selected={selectOption.sessionName === session.sessionName}
                className={`
                  ${selectOption.sessionName === session.sessionName ? 'bg-neutral-100' : 'bg-white'}
                  ${focusedIndex === index ? 'outline outline-2 outline-[var(--primary)] outline-offset-[-2px]' : ''}
                  h-[44px] relative shrink-0 w-full cursor-pointer hover:bg-neutral-100 transition-colors
                `}
                onClick={() => handleOptionClick(selectOption.sessionName)}
                onMouseEnter={() => setFocusedIndex(index)}
              >
                <div className="overflow-clip size-full">
                  <div className="box-border content-stretch flex flex-col gap-[2px] h-[44px] items-start p-[4px] relative w-full">
                    <div className="basis-0 content-stretch flex grow items-center min-h-px min-w-px relative shrink-0 w-full">
                      <div className="basis-0 grow min-h-px min-w-px relative shrink-0">
                        <div className="size-full">
                          <div className="box-border content-stretch flex items-start px-[2px] py-0 relative w-full">
                            <div className="basis-0 flex flex-col font-['Open_Sans:Regular',sans-serif] font-normal grow justify-center leading-[0] min-h-px min-w-px relative shrink-0 text-[#212121] text-[16px]" style={{ fontVariationSettings: "'wdth' 100" }}>
                              <p className="leading-[normal]">
                                {selectOption.sessionDescription}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 3D Alignment View 1 */}
      <div
        className="relative h-[380px] flex items-center justify-center"
        style={{ backgroundColor: '#E8EAED' }}
      >
        {signedUrl ? (
          <div onMouseEnter={
            () => logTrace('AchievedCorrection', 'proprio_view_corr_mesh_', session.sessionNameShorthand || '')
          }>
            <MeshViewer
              signedUrl={signedUrl}
              selectedNames={selectedVertebraeNames}
              selectedPlaneNames={selectedPlaneNames}
            />
          </div>
        ) : (
          <div style={{
            height: '350px',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f0f0f0',
            color: '#666'
          }}>
            3D View not available
          </div>
        )}
      </div>
    </React.Fragment>

  );
};

export default SpineViewer;
